import { useState, useEffect, useContext } from "react";
import { SocketContext } from "../context/Socket/SocketContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { getAgentMode, requestTakeover } from "../services/agentService";
import toastError from "../errors/toastError";
import toastUtils from "../utils/toastUtils";
import { i18n } from "../translate/i18n";

export const useAgentMode = (ticket, options = {}) => {
  const [agentMode, setAgentMode] = useState(null);
  const [isAgentActive, setIsAgentActive] = useState(false);
  const [canTakeover, setCanTakeover] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  
  const socket = useContext(SocketContext);
  const { user } = useContext(AuthContext);

  // Fetch agent mode info when ticket changes
  useEffect(() => {
    if (!ticket || !ticket.id) {
      setAgentMode(null);
      setIsAgentActive(false);
      setCanTakeover(false);
      return;
    }

    const fetchAgentMode = async () => {
      try {
        const response = await getAgentMode(ticket.id);
        setAgentMode(response.mode);
        setIsAgentActive(response.isActive);
        setCanTakeover(response.canTakeover);
      } catch (error) {
        console.warn("Could not fetch agent mode:", error);
        setAgentMode(null);
        setIsAgentActive(false);
        setCanTakeover(false);
      }
    };

    fetchAgentMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket && ticket.id]);

  // WebSocket listeners for agent suggestions
  useEffect(() => {
    if (!socket || !ticket || !ticket.id || !user || !user.companyId ||
        typeof socket.on !== 'function' || typeof socket.off !== 'function') {
      return;
    }

    const handleAgentSuggestions = (data) => {
      console.log("🤖 Evento agentSuggestions recebido:", data);
      if (data.ticketId === ticket.id) {
        console.log("✅ Sugestões para ticket correto:", data.suggestions);
        const newSuggestions = data.suggestions || [];
        setSuggestions(newSuggestions);
        setIsLoadingSuggestions(false);
        
        // Chamar callback se novas sugestões chegaram
        if (newSuggestions.length > 0 && options.onNewSuggestions) {
          options.onNewSuggestions(newSuggestions);
        }
      } else {
        console.log("❌ Sugestões para ticket diferente:", data.ticketId, "vs", ticket.id);
      }
    };

    const handleSuggestionAccepted = (data) => {
      if (data.ticketId === ticket.id) {
        // Remove accepted suggestion from list
        setSuggestions(prev => 
          prev.filter(s => s.id !== data.suggestionId)
        );
        toastUtils.success(i18n.t("agentSuggestions.suggestionSent"), "Agente Ativo");
      }
    };

    const handleSuggestionRejected = (data) => {
      if (data.ticketId === ticket.id) {
        // Remove rejected suggestion from list
        setSuggestions(prev => 
          prev.filter(s => s.id !== data.suggestionId)
        );
      }
    };

    const handleAgentModeChanged = (data) => {
      if (data.ticketId === ticket.id) {
        setAgentMode(data.mode);
        setIsAgentActive(data.isActive);
        setCanTakeover(data.canTakeover);
      }
    };

    // Setup WebSocket listeners - MÚLTIPLOS FORMATOS POSSÍVEIS
    try {
      const eventNames = [
        `company-${user.companyId}-agentSuggestions`,
        "agentSuggestions",
        `ticket-${ticket.id}-agentSuggestions`,
        "agent-suggestions",
        `company-${user.companyId}-agent-suggestions`,
      ];

      eventNames.forEach(eventName => {
        socket.on(eventName, handleAgentSuggestions);
        console.log("📡 Listener registrado:", eventName);
      });

      socket.on(`company-${user.companyId}-agentSuggestionAccepted`, handleSuggestionAccepted);
      socket.on(`company-${user.companyId}-agentSuggestionRejected`, handleSuggestionRejected);
      socket.on(`company-${user.companyId}-agentModeChanged`, handleAgentModeChanged);
    
      console.log("🔌 Agent mode listeners registrados para company:", user.companyId, "ticket:", ticket.id);

      // Debug: Capturar TODOS os eventos do socket
      const originalEmit = socket.onevent;
      socket.onevent = function(packet) {
        const eventName = packet.data[0];
        const eventData = packet.data[1];

        // Log apenas eventos relacionados à IA/sugestões
        if (eventName.toLowerCase().includes('agent') ||
            eventName.toLowerCase().includes('suggest') ||
            eventName.toLowerCase().includes('ia') ||
            eventName.toLowerCase().includes('ai')) {
          console.log("🎯 Evento IA detectado:", eventName, eventData);
        }

        // Chamar o handler original
        return originalEmit.call(socket, packet);
      };

      // Set loading when agent is active and might send suggestions
      if ((agentMode === "AGENT_ASSISTANT" || agentMode === "agent_assistant") && isAgentActive) {
        setIsLoadingSuggestions(true);
        console.log("⏳ Loading sugestões ativado");
      }

      return () => {
        try {
          // Cleanup todos os event listeners
          eventNames.forEach(eventName => {
            socket.off(eventName, handleAgentSuggestions);
          });
          socket.off(`company-${user.companyId}-agentSuggestionAccepted`, handleSuggestionAccepted);
          socket.off(`company-${user.companyId}-agentSuggestionRejected`, handleSuggestionRejected);
          socket.off(`company-${user.companyId}-agentModeChanged`, handleAgentModeChanged);
          console.log("🔌 Agent listeners removidos");
        } catch (error) {
          console.error('Erro ao remover listeners do agent mode:', error);
        }
      };
    } catch (error) {
      console.error('Erro ao configurar listeners do agent mode:', error);
      return () => {}; // Return empty cleanup function on error
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, ticket && ticket.id, user && user.companyId, agentMode, isAgentActive]);

  // Handle human takeover from AI agent
  const handleTakeover = async () => {
    if (!ticket || !ticket.id || !canTakeover) return;

    try {
      await requestTakeover(ticket.id);
      toastUtils.success(i18n.t("agentMode.takeoverSuccess"), "Modo Alterado");
      
      // Update local state immediately
      setAgentMode("none");
      setIsAgentActive(false);
      setCanTakeover(false);
      setSuggestions([]);
    } catch (error) {
      console.error("Takeover failed:", error);
      toastError(error);
    }
  };

  return {
    agentMode,
    isAgentActive,
    canTakeover,
    suggestions,
    isLoadingSuggestions,
    handleTakeover,
    setSuggestions, // Expor para poder limpar manualmente
  };
};